/**
 * Fault Groups & Categories — the NCR fault taxonomy (Admin Setup > Quality).
 * Extracted from QualityTab (CR-7320) to keep that file under 500 lines; the
 * ERP fault-code mapping beside it points into this taxonomy.
 */
import { useEffect, useState } from 'react';
import { qualityAPI, fetchAllPages } from '../../../lib/api';
import type { FaultCodeGroup, FaultCodeCategory } from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Loader2, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

interface FaultTaxonomySectionProps {
  /** Called after a group or category is added or removed (the mapping's choices change). */
  onChanged?: () => void;
}

export default function FaultTaxonomySection({ onChanged }: FaultTaxonomySectionProps) {
  // Fault groups & categories
  const [faultGroups, setFaultGroups] = useState<FaultCodeGroup[]>([]);
  const [categories, setCategories] = useState<FaultCodeCategory[]>([]);
  const [expandedGroup, setExpandedGroup] = useState<number | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Walk pages — the backend clamps page_size, so one request silently truncated.
    Promise.all([
      fetchAllPages<FaultCodeGroup>((page) => qualityAPI.getFaultGroups({ page_size: 100, page })),
      fetchAllPages<FaultCodeCategory>((page) => qualityAPI.getFaultCategories({ page_size: 100, page })),
    ])
      .then(([groups, cats]) => { setFaultGroups(groups); setCategories(cats); })
      .catch((error: unknown) => {
        console.error('Error loading fault taxonomy:', error);
        toast.error('Failed to load fault groups and categories');
      })
      .finally(() => setLoading(false));
  }, []);

  // Fault Group CRUD
  const handleAddGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      setAddingGroup(true);
      const resp = await qualityAPI.createFaultGroup({ name });
      setFaultGroups(prev => [...prev, resp.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewGroupName('');
      toast.success(`Added group "${name}"`);
      onChanged?.();
    } catch (error) {
      console.error('Error adding fault group:', error);
      toast.error('Failed to add fault group');
    } finally {
      setAddingGroup(false);
    }
  };

  const handleDeleteGroup = async (id: number, name: string) => {
    try {
      await qualityAPI.deleteFaultGroup(id);
      setFaultGroups(prev => prev.filter(g => g.id !== id));
      setCategories(prev => prev.filter(c => c.fault_group !== id));
      toast.success(`Removed group "${name}"`);
      onChanged?.();
    } catch {
      toast.error('Cannot delete - may be in use by existing NCRs');
    }
  };

  // Fault Category CRUD
  const handleAddCategory = async (groupId: number) => {
    const name = newCategoryName.trim();
    if (!name) return;
    try {
      setAddingCategory(true);
      const resp = await qualityAPI.createFaultCategory({ name, fault_group: groupId });
      setCategories(prev => [...prev, resp.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCategoryName('');
      toast.success(`Added category "${name}"`);
      onChanged?.();
    } catch (error) {
      console.error('Error adding fault category:', error);
      toast.error('Failed to add fault category');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleDeleteCategory = async (id: number, name: string) => {
    try {
      await qualityAPI.deleteFaultCategory(id);
      setCategories(prev => prev.filter(c => c.id !== id));
      toast.success(`Removed category "${name}"`);
      onChanged?.();
    } catch {
      toast.error('Cannot delete - may be in use by existing NCRs');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Fault Groups & Categories</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {faultGroups.map(group => {
            const groupCategories = categories.filter(c => c.fault_group === group.id);
            const isExpanded = expandedGroup === group.id;

            return (
              <div key={group.id} className="border rounded-md">
                <div
                  className="flex items-center justify-between py-2 px-3 cursor-pointer hover:bg-muted/50"
                  onClick={() => setExpandedGroup(isExpanded ? null : group.id)}
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <span className="text-sm font-medium">{group.name}</span>
                    <span className="text-xs text-muted-foreground">({groupCategories.length} categories)</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id, group.name); }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {isExpanded && (
                  <div className="border-t px-6 py-3 space-y-2 bg-muted/30">
                    {groupCategories.map(cat => (
                      <div key={cat.id} className="flex items-center justify-between py-1.5 px-3 bg-background rounded-md">
                        <span className="text-sm">{cat.name}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteCategory(cat.id, cat.name)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    {groupCategories.length === 0 && (
                      <p className="text-xs text-muted-foreground">No categories yet.</p>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(group.id); }}
                        placeholder="New category name..."
                        className="flex-1 h-8 text-sm"
                      />
                      <Button
                        onClick={() => handleAddCategory(group.id)}
                        disabled={addingCategory || !newCategoryName.trim()}
                        size="sm"
                        className="h-8"
                      >
                        {addingCategory ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                        Add
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {faultGroups.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">No fault groups configured yet.</p>
          )}
          <div className="flex gap-2 pt-2">
            <Label className="sr-only">New fault group</Label>
            <Input
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddGroup(); }}
              placeholder="New fault group name..."
              className="flex-1"
            />
            <Button onClick={handleAddGroup} disabled={addingGroup || !newGroupName.trim()} size="sm">
              {addingGroup ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add Group
            </Button>
          </div>
        </CardContent>
      </Card>
  );
}
